import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import PropTypes from 'prop-types'
import {
  PayPalScriptProvider,
  PayPalHostedFieldsProvider,
  PayPalHostedField,
  usePayPalHostedFields,
} from '@paypal/react-paypal-js'

import { config } from 'hungry-core2'
import LoadingIndicator from '@components/common/LoadingIndicator'
import { Input } from '@components/common/form'

const CUSTOM_FIELD_STYLE = {
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
  padding: '8px 12px',
  fontSize: '16px',
  width: '100%',
  transition: 'border-color 0.2s ease',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  height: '40px',
  boxSizing: 'border-box',
}

const INVALID_COLOR = {
  color: '#dc3545',
  marginLeft: '4px',
}

const LABEL_STYLE = {
  display: 'block',
  marginBottom: '8px',
  fontSize: '14px',
  fontWeight: '500',
  color: '#333333',
}

const CONTAINER_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  maxWidth: '400px',
  margin: '0 auto',
  marginTop: '16px',
  width: '100%',
}

const BUTTON_STYLE = {
  padding: '10px 30px',
  borderRadius: '15px',
  minWidth: '200px',
  width: 'fit-content',
  color: '#fff',
  fontFamily: 'ProximaNova-Light',
  fontSize: '20px',
  border: 'none',
  background: 'linear-gradient(to right, #c0a453 0%, #e5c063 100%)',
  display: 'block',
  cursor: 'pointer',
  fontWeight: 'bold',
}

const BUTTON_DISABLED_STYLE = {
  ...BUTTON_STYLE,
  background: '#cccccc',
  cursor: 'not-allowed',
}

const LOADER_STYLE = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '20px',
}

const SaveCard = forwardRef(
  ({ displayFailureMessage, finalizePaypalVaultOrder, onSave }, ref) => {
    const cardHolderNameRef = useRef(null)
    const [billingAddress, setBillingAddress] = useState({})
    const hostedField = usePayPalHostedFields()
    const [processing, setProcessing] = useState(false)

    useImperativeHandle(ref, () => ({
      getExtraDetails: () => {
        return {
          cardholderName: cardHolderNameRef?.current?.value,
          billingAddress:
            Object.keys(billingAddress).length > 0 ? billingAddress : null,
        }
      },
    }))

    const handleSubmit = async () => {
      if (!hostedField?.cardFields) {
        const childErrorMessage =
          'Unable to find any child components in the <PayPalHostedFieldsProvider />'
        displayFailureMessage(childErrorMessage)

        return
      }

      const cardholderName = cardHolderNameRef?.current?.value
      if (!cardholderName) {
        displayFailureMessage('Please enter a valid cardholder name')

        return
      }

      const invalidCardFields = Object.entries(
        hostedField.cardFields.getState().fields,
      )
        .filter(([, field]) => !field.isValid)
        .map(([key]) => key)

      if (invalidCardFields.includes('number')) {
        displayFailureMessage('Please enter a valid card number')

        return
      }

      if (invalidCardFields.includes('expirationDate')) {
        displayFailureMessage('Please enter a valid expiration date')

        return
      }

      if (invalidCardFields.includes('cvv')) {
        displayFailureMessage('Please enter a valid CVV')

        return
      }

      setProcessing(true)
      try {
        const { orderId } = await hostedField.cardFields.submit({
          cardholderName,
          vault: true,
        })

        const response = await finalizePaypalVaultOrder({ orderId })
        console.log('finalize vault', response)
        if (typeof response === 'string') {
          displayFailureMessage(response)
        } else {
          const {
            capture: { lastDigits, brand, paymentMethodId, expiry },
          } = response

          await onSave({
            cardholderName,
            token: paymentMethodId,
            billingAddress,
            last4: lastDigits,
            cardType: brand,
            gateway: 'paypal',
            expirationDate: expiry,
          })
        }
      } catch (err) {
        displayFailureMessage('Failed to read card details:  ' + err)
      } finally {
        setProcessing(false)
      }
    }

    const onUpdateBillingAddress = (fieldName) => (e) => {
      const nextValue = e.target.value
      const nextBillingAddress = { ...billingAddress }
      nextBillingAddress[fieldName] = nextValue
      setBillingAddress(nextBillingAddress)
    }

    return (
      <div style={CONTAINER_STYLE}>
        <div>
          <label
            style={LABEL_STYLE}
            title="This represents the full name as shown in the card"
          >
            Card Holder Name
          </label>
          <input
            id="card-holder"
            ref={cardHolderNameRef}
            className="card-field"
            style={{ ...CUSTOM_FIELD_STYLE }}
            type="text"
            placeholder="Name as it appears on the card"
          />
        </div>

        <div>
          <label style={LABEL_STYLE}> Billing Address </label>
          <Input
            label="Address Line 1"
            value={billingAddress.line1 || ''}
            onChange={onUpdateBillingAddress('line1')}
          />
          <Input
            label="Address Line 2"
            value={billingAddress.line2 || ''}
            onChange={onUpdateBillingAddress('line2')}
          />
          <Input
            label="City"
            value={billingAddress.city || ''}
            onChange={onUpdateBillingAddress('city')}
          />
          <Input
            label="State"
            value={billingAddress.state || ''}
            onChange={onUpdateBillingAddress('state')}
          />
          <Input
            label="Zip"
            value={billingAddress.zip || ''}
            onChange={onUpdateBillingAddress('zip')}
          />
          <Input
            label="Country"
            value={billingAddress.country || ''}
            onChange={onUpdateBillingAddress('country')}
          />
        </div>

        {processing ? (
          <div style={LOADER_STYLE}>
            <LoadingIndicator />
          </div>
        ) : (
          <button
            style={processing ? BUTTON_DISABLED_STYLE : BUTTON_STYLE}
            type="submit"
            onClick={handleSubmit}
            disabled={processing}
          >
            <span>Validate Card</span>
          </button>
        )}
      </div>
    )
  },
)

SaveCard.propTypes = {
  displayFailureMessage: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  finalizePaypalVaultOrder: PropTypes.func.isRequired,
}

SaveCard.displayName = 'SaveCard'

// Remove memoization and simplify component
const PaypalPaymentMethodInput = ({
  getPaypalClientToken,
  createPaypalVaultOrder,
  finalizePaypalVaultOrder,
  country,
  currency,
  displayInfoMessage,
  displayFailureMessage,
  onSave,
}) => {
  const [clientToken, setClientToken] = useState(null)
  const submitRef = useRef(null)

  useEffect(() => {
    if (!clientToken) {
      ;(async () => {
        const token = await getPaypalClientToken()
        if (token) {
          setClientToken(token)
        }
      })()
    }
  }, [getPaypalClientToken, clientToken])

  if (!clientToken) {
    return <h1>Loading token...</h1>
  }

  return (
    <PayPalScriptProvider
      options={{
        clientId: config.paypal_client_id,
        components: 'buttons,hosted-fields',
        dataClientToken: clientToken,
        intent: 'capture',
        vault: true,
      }}
    >
      <PayPalHostedFieldsProvider
        styles={{
          '.valid': { color: '#28a745' },
          '.invalid': { color: '#dc3545' },
          input: {
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontSize: '16px',
            padding: '8px 12px',
            borderRadius: '4px',
            border: '1px solid #e0e0e0',
            backgroundColor: '#ffffff',
            transition: 'border-color 0.2s ease',
            height: '40px',
            boxSizing: 'border-box',
          },
        }}
        createOrder={async () => {
          let extraDetails = null
          if (submitRef.current) {
            extraDetails = submitRef.current.getExtraDetails()
          }
          console.log('extraDetails', extraDetails)
          const order = await createPaypalVaultOrder({
            currency,
            cardholderName: extraDetails?.cardholderName,
            billingAddress: { ...extraDetails?.billingAddress, country },
          })
          if (!order) {
            displayFailureMessage('Failed to create PayPal order')

            return
          }

          return order.id
        }}
      >
        <div style={CONTAINER_STYLE}>
          <div>
            <label style={LABEL_STYLE} htmlFor="card-number">
              Card Number
              <span style={INVALID_COLOR}>*</span>
            </label>
            <PayPalHostedField
              id="card-number"
              className="card-field"
              style={CUSTOM_FIELD_STYLE}
              hostedFieldType="number"
              options={{
                selector: '#card-number',
                placeholder: '4111 1111 1111 1111',
              }}
            />
          </div>

          <div>
            <label style={LABEL_STYLE} htmlFor="cvv">
              CVV<span style={INVALID_COLOR}>*</span>
            </label>
            <PayPalHostedField
              id="cvv"
              className="card-field"
              style={CUSTOM_FIELD_STYLE}
              hostedFieldType="cvv"
              options={{
                selector: '#cvv',
                placeholder: '123',
                maskInput: true,
              }}
            />
          </div>

          <div>
            <label style={LABEL_STYLE} htmlFor="expiration-date">
              Expiration Date
              <span style={INVALID_COLOR}>*</span>
            </label>
            <PayPalHostedField
              id="expiration-date"
              className="card-field"
              style={CUSTOM_FIELD_STYLE}
              hostedFieldType="expirationDate"
              options={{
                selector: '#expiration-date',
                placeholder: 'MM/YYYY',
              }}
            />
          </div>
        </div>
        <SaveCard
          ref={submitRef}
          displayInfoMessage={displayInfoMessage}
          displayFailureMessage={displayFailureMessage}
          finalizePaypalVaultOrder={finalizePaypalVaultOrder}
          onSave={onSave}
        />
      </PayPalHostedFieldsProvider>
    </PayPalScriptProvider>
  )
}

PaypalPaymentMethodInput.propTypes = {
  getPaypalClientToken: PropTypes.func.isRequired,
  createPaypalVaultOrder: PropTypes.func.isRequired,
  finalizePaypalVaultOrder: PropTypes.func.isRequired,
  country: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  displayFailureMessage: PropTypes.func.isRequired,
  displayInfoMessage: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
}

export default PaypalPaymentMethodInput
